import { useReducer, useState, useEffect } from "react";
import TrailerSearchSelect from "../../Components/Inputs/TrailerSearch";
import handleTrailerSelect from "../../Components/Inputs/TrailerSearchHelper";
import { useTranslation } from "react-i18next";
import { mexicanStates, usStates } from "../../Helper/CountryStates";
import { FormInput, FormSelect } from "../../Components/Inputs/FormInputs";
import {
  COUNTRY_OPTIONS,
  VEHICLE_CONDITIONS,
} from "../../Constants/CheckInOut";
import instance from "../../Interceptor/api_instance";
import { useFetch } from "../../Hooks/useFetch";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import { useApiMutation } from "../../Hooks/useApiMutation";

export const getOriginData = (origin) => {
  if (!origin) {
    return "";
  }
  const [city, state, country] = origin.split(", ");
  return [city, state, country];
};

const reducer = (state, action) => {
  switch (action.type) {
    case "IS_TRAILER_ATI":
      return {
        ...state,
        isTrailerAti: action.payload,
      };
    case "ERROR_MESSAGE":
      return {
        ...state,
      };
    case "SET_TRAILER_DIMENSION":
      return {
        ...state,
        trailerDimension: action.payload,
      };
    case "SUBMIT":
      return {
        ...state,
        submitLoading: true,
      };
    case "SUBMIT_RESPONSE":
      return {
        ...state,
        resMessage: action.resMessage,
        resStatus: action.resStatus,
        submitLoading: false,
      };
  }
};

const initialState = {
  isTrailerAti: null,
  trailerErrorMessage: null,
  trailerDimension: null,
  submitLoading: false,
  resMessage: null,
  resStatus: null,
};

export const EditTrailerRow = ({ close, row, onSuccess }) => {
  const { t } = useTranslation();

  // Fetch trailer detail by ID
  const { data, isLoading, error } = useFetch(
    ({ signal }) =>
      instance
        .get(`assets/trailers/${row.trailer.id}/`, { signal })
        .then((res) => res.data),
    [row.trailer.id]
  );

  const {
    mutate: updateTrailer,
    isLoading: updateLoading,
    error: updateError,
  } = useApiMutation(async (payload) => {
    const response = await instance.put("assets/edit-trailer", payload);
    return response;
  });

  const [trailerData, setTrailerData] = useState({});

  // Populate form state once fetch completes
  useEffect(() => {
    if (data) {
      const [city, state, country] = getOriginData(data.origin);
      setTrailerData({
        id: row.id,
        trailerPlate: data.license_plate,
        trailerNumber: data.trailer_number,
        trailerDimensions: data.trailer_dimensions,
        isLoaded: data.is_loaded,
        customer: data.customer,
        sealNumber: data.seal_number,
        checkOutSealNumber: data.check_out_seal_number,
        trailerCondition: data.trailer_condition,
        trailerConditionNotes: data.trailer_condition_notes,
        trailerAsset: data.trailer_asset ? data.trailer_asset.id : null,
        trailerOption: data.trailer_asset
          ? { label: data.trailer_number, value: data.trailer_asset.id }
          : null,
        city: city,
        state: state,
        country: country,
      });
      dispatchForm({
        type: "IS_TRAILER_ATI",
        payload: data.trailer_asset ? true : false,
      });
    }
  }, [data]);

  // Condition states
  const [formState, dispatchForm] = useReducer(reducer, {
    ...initialState,
    isTrailerAti: data?.trailer_asset ? true : false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrailerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const postEditChanges = async (e) => {
    e.preventDefault();
    const payload = {
      id: trailerData.id,
      trailer_asset: trailerData.trailerAsset,
      trailer_number: trailerData.trailerNumber,
      is_loaded: trailerData.isLoaded,
      customer: trailerData.customer,
      origin: `${trailerData.city}, ${trailerData.state}, ${trailerData.country}`,
      license_plate: trailerData.trailerPlate,
      seal_number: trailerData.sealNumber,
      check_out_seal_number: trailerData.checkOutSealNumber,
      trailer_condition: trailerData.trailerCondition,
      trailer_condition_notes: trailerData.trailerConditionNotes,
    };

    try {
      const response = await updateTrailer(payload);
      if (response.status === 200) {
        onSuccess(response.data, "trailer");
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (error) return <p className="error">{error}</p>;
  return (
    <div className="page-mask page-mask-animation">
      <div className="edit-modal">
        {isLoading ? (
          <Spinner />
        ) : (
          <div>
            <h4 className="global-h2">{t("forms.type.edit-entry")}</h4>
            <p className="gray">{trailerData.trailerNumber}</p>
            <form onSubmit={(e) => postEditChanges(e)}>
              {/* Form group: Is trailer ATI */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.ati-bool")}{" "}
                </label>
                <div className="input-cont">
                  <fieldset className="fieldsets">
                    <div className="radio-options">
                      <input
                        type="radio"
                        name="group1"
                        className="radio-input"
                        checked={formState.isTrailerAti}
                        onChange={() => {
                          dispatchForm({
                            type: "IS_TRAILER_ATI",
                            payload: true,
                          });
                          setTrailerData((prev) => ({
                            ...prev,
                            trailerNumber: null,
                            trailerPlate: "",
                          }));
                        }}
                        required
                      />
                      <label>{t("general.yes")}</label>
                    </div>
                    <div className="radio-options">
                      <input
                        type="radio"
                        name="group1"
                        className="radio-input"
                        checked={formState.isTrailerAti === false}
                        onChange={() => {
                          dispatchForm({
                            type: "IS_TRAILER_ATI",
                            payload: false,
                          });
                          setTrailerData((prev) => ({
                            ...prev,
                            trailerNumber: null,
                            trailerPlate: "",
                            trailerAsset: null,
                            trailerOption: null,
                          }));
                        }}
                      />
                      <label>No</label>
                    </div>
                  </fieldset>
                </div>
              </div>

              {/* Form group: Trailer number */}
              <div className="form-group">
                {formState.isTrailerAti ? (
                  <>
                    <label className="input-label">
                      {t("forms.type.vehicle.placeholders.select-trailer")}
                    </label>
                    <div className="input-cont">
                      <TrailerSearchSelect
                        onSelect={(option) =>
                          handleTrailerSelect({
                            selectedTrailer: option,
                            setFormData: setTrailerData,
                            dispatchForm,
                          })
                        }
                        selectedTrailer={trailerData.trailerOption}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="trailerNum" className="input-label">
                        {t("forms.type.vehicle.input-headers.trailer-number")}
                      </label>
                      <div className="input-cont">
                        <input
                          type="text"
                          value={trailerData.trailerNumber}
                          className="text-input"
                          name="trailerNumber"
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Form group: license plate */}
              <FormInput
                label={"trailerPlate"}
                value={trailerData.trailerPlate}
                onChange={handleInputChange}
              />

              {/* Form group: Is loaded */}
              <FormSelect
                options={[true, false]}
                label={"isLoaded"}
                value={trailerData.isLoaded}
                onChange={handleInputChange}
              />

              {/* Form group: customer */}
              <div className="form-group">
                <label className="input-label">
                  {t("yard-tables.search-filters.dropdowns.customer")}
                </label>
                <div className="input-cont">
                  <input
                    type="text"
                    name="customer"
                    className="text-input"
                    value={trailerData.customer}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Form group: seal number */}
              <FormInput
                label={"sealNumber"}
                value={trailerData.sealNumber}
                onChange={handleInputChange}
                required={false}
              />

              {/* Form group: exit seal number */}
              <FormInput
                label={"checkOutSealNumber"}
                value={trailerData.checkOutSealNumber}
                onChange={handleInputChange}
                required={false}
              />

              {/* Form group: Condition */}
              <FormSelect
                label={"trailerCondition"}
                options={VEHICLE_CONDITIONS}
                value={trailerData.trailerCondition}
                onChange={handleInputChange}
              />

              {/* Form group: Trailer condition notes */}
              <FormInput
                label={"trailerConditionNotes"}
                value={trailerData.trailerConditionNotes}
                onChange={handleInputChange}
                required={
                  trailerData.trailerCondition?.toLowerCase() !== "good" &&
                  trailerData.trailerCondition?.toLowerCase() !== "okay" &&
                  trailerData.trailerCondition?.toLowerCase() !== "ready"
                }
              />

              {/* Form group: Origin */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.origin")}
                </label>
              </div>
              <FormSelect
                options={COUNTRY_OPTIONS}
                label={"country"}
                value={trailerData.country}
                onChange={handleInputChange}
              />

              {/* Form group: State */}
              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.labels.state")}
                </label>
                <div className="input-cont">
                  <select
                    id="state"
                    name="state"
                    data-testid="state"
                    value={trailerData.state}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" selected disabled>
                      {t("forms.type.vehicle.placeholders.destination.state")}
                    </option>
                    {trailerData.country?.toLowerCase() === "usa"
                      ? usStates.map((state, index) => (
                          <option key={index} value={state}>
                            {state}
                          </option>
                        ))
                      : mexicanStates.map((state, index) => (
                          <option key={index} value={state}>
                            {state}
                          </option>
                        ))}
                  </select>
                </div>
              </div>

              {/* Form group: City */}
              <FormInput
                label={"city"}
                value={trailerData.city}
                onChange={handleInputChange}
              />

              {updateError && <p className="error">{updateError}</p>}
              <div className="full-cont">
                {updateLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <button type="submit" className="other-btn">
                      {t("buttons.update")}
                    </button>
                    <button onClick={close} className="close-btn">
                      {t("buttons.close")}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
